// Pure, UI-independent signal engine. Time is driven by event.t and tick(t) (ms).
export const DEFAULT_THRESHOLDS = {
  ccNumber: 90,
  ccBaseline: 0,
  ccNoiseFloor: 4,
  ccMax: 127,
  noiseVelocity: 5,
  pulseVelocity: 90,
  pulseCcJump: 25,
  pulseCcWindowMs: 150,
  refractoryMs: 250,
  strokeMinDurationMs: 1000,
  strokeMinEvents: 4,
  strokeGapMs: 600,
  strokeIntervalMs: 1000,
  stillThresholdsS: [3, 6, 10],
  activityRateMax: 15,
  agitationWindowMs: 2500,
  ccSlopeMax: 400,
  intensityTauS: 0.25,
  activityTauS: 0.6,
  agitationTauS: 0.5,
  calmTauS: 0.8,
  noteEnvelopeTauS: 0.35,
  gentleVelMax: 70,
  gentleCcJumpMax: 12,
  strictness: 3,
  strokeSuppressMs: 300,
  pulseMergeMs: 60,
  brushMinMs: 450,
};

// Pulse strictness 1 (loose) .. 5 (strict). Margins above the calibrated gentle range,
// threshold boost while stroking (hysteresis) and min confidence for a "solid" pulse.
export const STRICTNESS_LEVELS = {
  1: { velMargin: 0, ccMargin: 0.1, strokeBoost: 1.1, minConfidence: 0.3 },
  2: { velMargin: 5, ccMargin: 0.25, strokeBoost: 1.2, minConfidence: 0.42 },
  3: { velMargin: 10, ccMargin: 0.4, strokeBoost: 1.3, minConfidence: 0.55 },
  4: { velMargin: 16, ccMargin: 0.6, strokeBoost: 1.4, minConfidence: 0.68 },
  5: { velMargin: 22, ccMargin: 0.8, strokeBoost: 1.5, minConfidence: 0.8 },
};

const clamp = (x, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, x));
const ema = (cur, target, dt, tau) => cur + (target - cur) * (1 - Math.exp(-dt / tau));
const pruneBefore = (arr, cutoff, key = (x) => x) => {
  while (arr.length && key(arr[0]) < cutoff) arr.shift();
};

export function createSignalEngine(thresholds = {}, { startT = 0 } = {}) {
  let cfg = { ...DEFAULT_THRESHOLDS, ...thresholds };
  const listeners = new Set();
  const state = { intensity: 0, activity: 0, agitation: 0, calm: 0, stillnessSeconds: 0, cc: null, lastVelocity: 0, stroking: false };

  let lastT, lastSignificantT, stillFired, ccValue, ccHistory, ccArmed, ccPeak, lastPulseT;
  let strokeStartT, lastGentleT, gentleCount, lastStrokeT, sigTimes, spikeTimes, recentVel, noteEnv, ccSlopeAcc, pending, brushEmitted;

  function reset(t = startT) {
    lastT = t;
    lastSignificantT = t;
    stillFired = new Set();
    ccValue = cfg.ccBaseline;
    ccHistory = [];
    ccArmed = true;
    ccPeak = 0;
    lastPulseT = -Infinity;
    strokeStartT = null;
    lastGentleT = -Infinity;
    gentleCount = 0;
    lastStrokeT = -Infinity;
    sigTimes = [];
    spikeTimes = [];
    recentVel = [];
    noteEnv = 0;
    ccSlopeAcc = 0;
    pending = null;
    brushEmitted = false;
    Object.assign(state, { intensity: 0, activity: 0, agitation: 0, calm: 0, stillnessSeconds: 0, cc: null, lastVelocity: 0, stroking: false });
  }
  reset(startT);

  const emit = (type, t, extra = {}) => {
    const ev = { type, t, ...extra };
    listeners.forEach((fn) => fn(ev));
  };

  function markSignificant(t) {
    lastSignificantT = t;
    stillFired.clear();
    sigTimes.push(t);
  }

  const inStroke = (t) => strokeStartT !== null && gentleCount >= 2 && t - lastGentleT <= cfg.strokeSuppressMs;

  function effThresholds(t) {
    const L = STRICTNESS_LEVELS[cfg.strictness] || STRICTNESS_LEVELS[3];
    const stroke = inStroke(t);
    const boost = stroke ? L.strokeBoost : 1;
    return {
      L,
      stroke,
      vel: Math.max(cfg.pulseVelocity, cfg.gentleVelMax + L.velMargin) * boost,
      cc: Math.max(cfg.pulseCcJump, cfg.gentleCcJumpMax * (1 + L.ccMargin) + 4) * boost,
    };
  }

  // Candidate spike: merged across note/CC for pulseMergeMs, then resolved into one PULSE.
  function candidate(t, kind, value, rise, th) {
    const sep = kind === 'velocity'
      ? clamp((value - cfg.gentleVelMax) / Math.max(10, 127 - cfg.gentleVelMax))
      : clamp((value - cfg.gentleCcJumpMax) / Math.max(10, 3 * cfg.gentleCcJumpMax));
    const thr = kind === 'velocity' ? th.vel : th.cc;
    const margin = clamp((value - thr) / Math.max(8, kind === 'velocity' ? 127 - thr : thr));
    const conf = 0.35 * sep + 0.35 * clamp(rise) + 0.3 * margin - (th.stroke ? 0.15 : 0);
    spikeTimes.push(t);
    if (pending) {
      pending.kinds.add(kind);
      pending.conf = Math.max(pending.conf, conf);
      return;
    }
    if (t - lastPulseT < cfg.refractoryMs) return;
    pending = { t, conf, kinds: new Set([kind]), value, minConf: th.L.minConfidence };
  }

  function resolvePending(t) {
    if (!pending || t - pending.t < cfg.pulseMergeMs) return;
    const p = pending;
    pending = null;
    const confidence = clamp(p.conf + (p.kinds.size > 1 ? 0.2 : 0));
    const solid = confidence >= p.minConf;
    lastPulseT = p.t;
    if (solid) {
      strokeStartT = null;
      gentleCount = 0;
    }
    emit('PULSE', p.t, { reason: [...p.kinds].join('+'), value: p.value, confidence: Math.round(confidence * 100) / 100, solid });
  }

  function evaluateStroke(t) {
    const active = strokeStartT !== null && t - lastGentleT <= cfg.strokeGapMs;
    if (active && !brushEmitted && gentleCount >= 2 && lastGentleT - strokeStartT >= cfg.brushMinMs) {
      brushEmitted = true;
      emit('BRUSH', t, { durationMs: Math.round(lastGentleT - strokeStartT) });
    }
    state.stroking = active && lastGentleT - strokeStartT >= cfg.strokeMinDurationMs && gentleCount >= cfg.strokeMinEvents;
    if (state.stroking && t - lastStrokeT >= cfg.strokeIntervalMs) {
      lastStrokeT = t;
      emit('STROKE', t, { durationMs: Math.round(t - strokeStartT) });
    }
  }

  function gentle(t) {
    if (t - lastPulseT < cfg.refractoryMs) return;
    if (strokeStartT === null || t - lastGentleT > cfg.strokeGapMs) {
      strokeStartT = t;
      gentleCount = 0;
      brushEmitted = false;
    }
    lastGentleT = t;
    gentleCount += 1;
    evaluateStroke(t);
  }

  function ingestNote(e) {
    state.lastVelocity = e.velocity;
    if (e.velocity < cfg.noiseVelocity) return;
    markSignificant(e.t);
    const prior = recentVel.filter((x) => e.t - x.t <= 400);
    const priorMean = prior.length ? prior.reduce((a, x) => a + x.v, 0) / prior.length : 0;
    recentVel.push({ t: e.t, v: e.velocity });
    noteEnv = Math.max(noteEnv, e.velocity / 127);
    const th = effThresholds(e.t);
    if (e.velocity >= th.vel) candidate(e.t, 'velocity', e.velocity, (e.velocity - priorMean) / 60, th);
    else if (pending && e.velocity >= cfg.gentleVelMax + th.L.velMargin) pending.kinds.add('velocity');
    else gentle(e.t);
  }

  function ingestCc(e) {
    const { t, value: v } = e;
    const prev = ccValue;
    const delta = v - prev;
    ccValue = v;
    state.cc = v;
    ccSlopeAcc += Math.abs(delta);
    pruneBefore(ccHistory, t - cfg.pulseCcWindowMs, (x) => x.t);
    const minRecent = Math.min(prev, ...ccHistory.map((x) => x.v));
    ccHistory.push({ t, v });
    const above = v - cfg.ccBaseline > cfg.ccNoiseFloor;
    const rising = delta > cfg.ccNoiseFloor;
    if (!ccArmed) {
      ccPeak = Math.max(ccPeak, v);
      if (ccPeak - v >= cfg.pulseCcJump / 2) ccArmed = true;
    }
    if (!above && !rising) return;
    markSignificant(t);
    const jump = v - minRecent;
    const th = effThresholds(t);
    if (ccArmed && jump >= th.cc) {
      ccArmed = false;
      ccPeak = v;
      candidate(t, 'cc', jump, jump / 50, th);
      return;
    }
    if (pending && jump >= cfg.gentleCcJumpMax * (1 + th.L.ccMargin)) {
      pending.kinds.add('cc');
      return;
    }
    if (above && delta >= -cfg.ccNoiseFloor && jump < th.cc) gentle(t);
  }

  function ingest(e) {
    if (e.type === 'note_on') ingestNote(e);
    else if (e.type === 'cc' && e.cc === cfg.ccNumber) ingestCc(e);
  }

  function velStd() {
    if (recentVel.length < 2) return 0;
    const m = recentVel.reduce((a, x) => a + x.v, 0) / recentVel.length;
    return Math.sqrt(recentVel.reduce((a, x) => a + (x.v - m) ** 2, 0) / recentVel.length);
  }

  function tick(t) {
    const dt = (t - lastT) / 1000;
    if (dt <= 0) return getState();
    lastT = t;
    resolvePending(t);
    pruneBefore(sigTimes, t - 1000);
    pruneBefore(spikeTimes, t - cfg.agitationWindowMs);
    pruneBefore(recentVel, t - cfg.agitationWindowMs, (x) => x.t);

    noteEnv *= Math.exp(-dt / cfg.noteEnvelopeTauS);
    const span = Math.max(1, cfg.ccMax - cfg.ccBaseline - cfg.ccNoiseFloor);
    const ccNorm = clamp((ccValue - cfg.ccBaseline - cfg.ccNoiseFloor) / span);
    state.intensity = ema(state.intensity, Math.max(ccNorm, noteEnv), dt, cfg.intensityTauS);

    state.activity = ema(state.activity, clamp(sigTimes.length / cfg.activityRateMax), dt, cfg.activityTauS);

    const spikeNorm = clamp(spikeTimes.length / (cfg.agitationWindowMs / 1000) / 2);
    const slopeNorm = clamp(ccSlopeAcc / dt / cfg.ccSlopeMax);
    ccSlopeAcc = 0;
    const agitationTarget = clamp(0.6 * spikeNorm + 0.25 * slopeNorm + 0.15 * clamp(velStd() / 40));
    state.agitation = ema(state.agitation, agitationTarget, dt, cfg.agitationTauS);

    const calmTarget = clamp(1 - state.agitation * 1.6) * clamp(state.activity * 4);
    state.calm = ema(state.calm, calmTarget, dt, cfg.calmTauS);

    evaluateStroke(t);

    state.stillnessSeconds = Math.max(0, (t - lastSignificantT) / 1000);
    cfg.stillThresholdsS.forEach((th) => {
      if (state.stillnessSeconds >= th && !stillFired.has(th)) {
        stillFired.add(th);
        emit('STILL', t, { seconds: th });
      }
    });
    return getState();
  }

  function getState() {
    return { ...state };
  }

  return {
    ingest,
    tick,
    getState,
    reset,
    on(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    getThresholds: () => ({ ...cfg, stillThresholdsS: [...cfg.stillThresholdsS] }),
    setThresholds(partial) {
      cfg = { ...DEFAULT_THRESHOLDS, ...partial, strictness: cfg.strictness };
    },
    setStrictness(level) {
      cfg = { ...cfg, strictness: Math.min(5, Math.max(1, Math.round(level))) };
    },
  };
}
